import { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Button,
  Box,
  CircularProgress,
  Fade,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../../../redux/store";

import {
  updateBillingAddress,
  setEditingBillingAddress,
} from "../../../../../redux/slices/projectsSlice";

import { US_STATES, COUNTRIES } from "../../../../../constants/address";

const BillingAddressEditForm = () => {
  const [fadeIn, setFadeIn] = useState(false);
  
  const {
    selectedProject,
    updateProjectBillingAddressStatus,
    updateProjectBillingAddressError,
    fetchProjectsStatus,
  } = useAppSelector((state) => state.projects) as any;
  const dispatch = useAppDispatch();

  useEffect(() => {
    setFadeIn(true);
  }, []);

  const [formData, setFormData] = useState({
    billAddress1: selectedProject?.billingAddress?.billAddress1 || "",
    billAddress2: selectedProject?.billingAddress?.billAddress2 || "",
    billAddressCity: selectedProject?.billingAddress?.billAddressCity || "",
    billAddressState: selectedProject?.billingAddress?.billAddressState || "",
    billAddressZip: selectedProject?.billingAddress?.billAddressZip || "",
    billAddressCountry:
      selectedProject?.billingAddress?.billAddressCountry || "US",
  });

  const handleFieldChange = (field: any) => (event: any) => {
    const value = event.target.value;
    const updatedData = {
      ...formData,
      [field]: value,
    };
    setFormData(updatedData);

    // Call parent onChange if provided
    // if (onChange) {
    //   onChange(field, value, updatedData);
    // }
  };

  const handleSave = () => {
    dispatch(
      updateBillingAddress({
        projectId: selectedProject.providerId,
        billingAddress: formData,
      })
    );
  };

  const handleCancel = () => {
    // Start fade out animation
    setFadeIn(false);
    
    // Delay the actual cancel action to allow fade out to complete
    setTimeout(() => {
      // Reset form to initial data
      setFormData({
        billAddress1: selectedProject?.billingAddress?.billAddress1 || "",
        billAddress2: selectedProject?.billingAddress?.billAddress2 || "",
        billAddressCity: selectedProject?.billingAddress?.billAddressCity || "",
        billAddressState: selectedProject?.billingAddress?.billAddressState || "",
        billAddressZip: selectedProject?.billingAddress?.billAddressZip || "",
        billAddressCountry:
          selectedProject?.billingAddress?.billAddressCountry || "US",
      });

      dispatch(setEditingBillingAddress(false));
    }, 500); // Match the fade timeout
  };

  const isFormValid =
    formData.billAddress1.trim() &&
    formData.billAddressCity.trim() &&
    formData.billAddressState.trim() &&
    formData.billAddressZip.trim() &&
    formData.billAddressCountry.trim();

  const loading = useMemo(
    () =>
      updateProjectBillingAddressStatus === "loading" ||
      fetchProjectsStatus === "loading",
    [updateProjectBillingAddressStatus, fetchProjectsStatus]
  );

  return (
    <Fade in={fadeIn}>
      <Card
        sx={{
          borderRadius: 0,
          position: "absolute",
          mr: 2,
          zIndex: 1,
          backgroundColor: "#f7f7f7",
        }}
      >
      <CardContent sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
          Edit Billing Address
        </Typography>

        <Grid container rowSpacing={2}>
          {/* Address Line 1 */}
          <Grid size={12}>
            <InputLabel htmlFor="billAddress1" sx={{ fontSize: 14, mb: 0.5 }}>
              Address Line 1 *
            </InputLabel>
            <TextField
              id="billAddress1"
              name="billAddress1"
              variant="outlined"
              size="small"
              fullWidth
              disabled={loading}
              error={!!updateProjectBillingAddressError}
              placeholder="Enter street address"
              value={formData.billAddress1}
              onChange={handleFieldChange("billAddress1")}
              autoComplete="address-line1"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                },
              }}
            />
          </Grid>

          {/* Address Line 2 */}
          <Grid size={12}>
            <InputLabel htmlFor="billAddress2" sx={{ fontSize: 14, mb: 0.5 }}>
              Address Line 2 (Optional)
            </InputLabel>
            <TextField
              id="billAddress2"
              name="billAddress2"
              variant="outlined"
              size="small"
              fullWidth
              disabled={loading}
              placeholder="Apartment, suite, unit, building, floor, etc."
              value={formData.billAddress2}
              onChange={handleFieldChange("billAddress2")}
              autoComplete="address-line2"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                },
              }}
            />
          </Grid>

          {/* City */}
          <Grid size={12}>
            <InputLabel
              htmlFor="billAddressCity"
              sx={{ fontSize: 14, mb: 0.5 }}
            >
              City *
            </InputLabel>
            <TextField
              id="billAddressCity"
              name="billAddressCity"
              variant="outlined"
              size="small"
              fullWidth
              disabled={loading}
              error={!!updateProjectBillingAddressError}
              placeholder="Enter city"
              value={formData.billAddressCity}
              onChange={handleFieldChange("billAddressCity")}
              autoComplete="address-level2"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                },
              }}
            />
          </Grid>

          {/* ZIP Code */}
          <Grid size={12}>
            <InputLabel htmlFor="billAddressZip" sx={{ fontSize: 14, mb: 0.5 }}>
              ZIP/Postal Code *
            </InputLabel>
            <TextField
              id="billAddressZip"
              name="billAddressZip"
              variant="outlined"
              size="small"
              fullWidth
              disabled={loading}
              error={!!updateProjectBillingAddressError}
              placeholder="Enter ZIP code"
              value={formData.billAddressZip}
              onChange={handleFieldChange("billAddressZip")}
              autoComplete="postal-code"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                },
              }}
            />
          </Grid>

          {/* Country */}
          <Grid size={12}>
            <InputLabel
              htmlFor="billAddressCountry"
              sx={{ fontSize: 14, mb: 0.5 }}
            >
              Country *
            </InputLabel>
            <FormControl fullWidth size="small">
              <Select
                id="billAddressCountry"
                name="billAddressCountry"
                value={formData.billAddressCountry}
                onChange={handleFieldChange("billAddressCountry")}
                disabled={loading}
                error={!!updateProjectBillingAddressError}
                autoComplete="country"
                sx={{
                  borderRadius: 0,
                }}
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* State */}
          <Grid size={12}>
            <InputLabel
              htmlFor="billAddressState"
              sx={{ fontSize: 14, mb: 0.5 }}
            >
              State/Province *
            </InputLabel>
            <FormControl fullWidth size="small">
              <Select
                id="billAddressState"
                name="billAddressState"
                value={formData.billAddressState}
                onChange={handleFieldChange("billAddressState")}
                disabled={loading}
                error={!!updateProjectBillingAddressError}
                autoComplete="address-level1"
                sx={{
                  borderRadius: 0,
                }}
              >
                {formData.billAddressCountry === "US" ? (
                  US_STATES.map((state) => (
                    <MenuItem key={state.code} value={state.code}>
                      {state.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">
                    <em>Enter manually for non-US addresses</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            {formData.billAddressCountry !== "US" && (
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                disabled={loading}
                error={!!updateProjectBillingAddressError}
                placeholder="Enter state/province"
                value={formData.billAddressState}
                onChange={handleFieldChange("billAddressState")}
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0,
                    mt: 1,
                  },
                }}
              />
            )}
          </Grid>

          {/* Action Buttons */}
          <Grid size={12}>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={loading || !isFormValid}
                size="small"
                sx={{
                  borderRadius: 0,
                  flex: 1,
                }}
                startIcon={loading ? <CircularProgress size={24} color="inherit" /> : null}
              >
                {loading ? "Saving..." : "Save Address"}
              </Button>
              <Button
                variant="outlined"
                // color="secondary"
                onClick={handleCancel}
                disabled={loading}
                size="small"
                sx={{
                  borderRadius: 0,
                  flex: 1,
                }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
    </Fade>
  );
};

export default BillingAddressEditForm;
